import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchMyLeads } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Cta } from "@/components/site/ui";
import { LogOut, Calendar, Home as HomeIcon, FileText } from "lucide-react";
import { Milestones } from "@/components/site/Milestones";

const badge = (s) =>
  s === "New" ? "bg-[#1C1A17]/15 text-[#7a5f30]" :
  s === "Contacted" ? "bg-amber-100 text-amber-800" :
  "bg-emerald-100 text-emerald-800";

const iconFor = (src) =>
  src === "consultation" ? Calendar : src === "quote" ? FileText : HomeIcon;

export default function Account() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { setLeads(await fetchMyLeads()); }
      catch { setLeads([]); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleLogout = async () => {
    await signOut();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F5F2ED]">
      <header className="bg-white border-b border-black/5">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="font-head text-xl font-extrabold tracking-tight">
            SPHERIO LIVING<span className="text-[#1C1A17]">.</span>
          </Link>
          <div className="flex items-center gap-4">
            {user?.picture && <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full object-cover" />}
            <div className="hidden sm:block text-sm">
              <div className="font-medium leading-tight" data-testid="account-name">{user?.name}</div>
              <div className="text-[#5C574F] text-xs">{user?.email}</div>
            </div>
            <button data-testid="account-logout" onClick={handleLogout} className="inline-flex items-center gap-2 text-sm text-[#5C574F] hover:text-[#1C1A17]">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 lg:px-10 py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#1C1A17]">My Account</div>
            <h1 className="font-head text-4xl sm:text-5xl font-medium tracking-tight mt-3">
              Welcome, {(user?.name || "").split(" ")[0] || "there"}.
            </h1>
            <p className="mt-3 text-[#5C574F] max-w-lg">
              Every consultation, quote and enquiry you send to Spherio Living lives here.
            </p>
          </div>
          <div className="flex gap-3">
            <Cta to="/book-consultation" data-testid="account-book-cta">Book new consultation</Cta>
            <Cta to="/instant-quote" variant="outline">Get instant quote</Cta>
          </div>
        </div>

        <h2 className="mt-14 font-head text-2xl font-medium">My projects</h2>

        {loading ? (
          <div className="mt-6 text-[#5C574F]">Loading your projects…</div>
        ) : leads.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-black/15 bg-white p-10 text-center">
            <div className="font-head text-xl">No projects yet</div>
            <p className="text-[#5C574F] mt-2 max-w-md mx-auto">
              Book a free consultation or use our instant quote tool to start your Spherio Living project.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Cta to="/book-consultation">Book consultation</Cta>
              <Cta to="/instant-quote" variant="outline">Instant quote</Cta>
            </div>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="account-projects">
            {leads.map((l) => {
              const Ico = iconFor(l.source);
              return (
                <div key={l.id} className="rounded-2xl bg-white border border-black/10 p-6" data-testid={`project-${l.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1C1A17]/10 text-[#1C1A17]">
                        <Ico className="h-5 w-5" />
                      </span>
                      <div>
                        <div className="font-medium capitalize">{l.source} request</div>
                        <div className="text-xs text-[#5C574F] mt-1">
                          {new Date(l.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                          {l.service ? ` · ${l.service}` : ""}
                        </div>
                      </div>
                    </div>
                    <span className={`text-xs font-semibold rounded-full px-3 py-1 ${badge(l.status)}`}>{l.status}</span>
                  </div>
                  {l.message && (
                    <p className="mt-4 text-sm text-[#5C574F] leading-relaxed line-clamp-3">{l.message}</p>
                  )}
                  <div className="mt-6 pt-5 border-t border-black/5">
                    <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#5C574F] mb-4">Project stage</div>
                    <Milestones stage={l.stage || "Consultation"} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
